import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "@xterm/xterm/css/xterm.css";
import { useQuery } from "convex/react";
import { useAuth } from "@clerk/react";
import { api, terminalWebSocketUrl } from "../api/client";
import { clerkFetch } from "../api/clerkFetch";
import { EgressApprovalCard } from "./EgressApprovalCard";
import { terminalsStore, setTerminalTitle } from "../stores/terminalsStore";
import { isNotOwnedRestoreRefusal, isStaleRestoreRefusal } from "../services/terminalContinuity";
import { api as convexApi } from "../lib/convexApi";
import { workbenchesStore } from "../stores/workbenchesStore";
import { PolicyChip } from "./PolicyChip";
import { TerminalContextMenu } from "./TerminalContextMenu";
import { registerTerminalPaste, pasteIntoTerminal } from "./workbench/terminalPasteRegistry";
import { pointInSelection } from "./workbench/terminalSelectionHitTest";
import { clipboardReadText, clipboardWriteText } from "../services/clipboard";
import { TwSelect } from "./TwSelect";
import { containsRtl } from "./terminalBidi";
import { getXtermTheme, subscribeThemeChange } from "../theme";
import { Plus, Maximize2, Minimize2, X } from "lucide-react";
import {
  layoutTilingTree,
  useTerminalLayout,
  type FloatingPosition,
  type LayoutMode,
  type PositionedTile,
  type TilingSplitter,
} from "../hooks/useTerminalLayout";
import type { TerminalMeta } from "../types";
import type { ResolveCtx, ResolvedRef } from "../utils/fileRefResolve";
import { registerFileLinkProvider } from "../utils/terminalFileLinks";

// Shared xterm font config — must be identical between the real terminal
// host (TerminalHost) and the spawn-time measurement (measureInitialDims),
// otherwise the measured char cell width won't match what the live tile
// actually renders. Menlo leads because it ships with every macOS install
// and has full Dingbats + box-drawing coverage — xterm.js picks one font
// at startup (no per-glyph CSS fallback), so the lead entry must be a
// guaranteed-present font with full glyph coverage.
const XTERM_FONT = {
  // Lead with the BUNDLED web font ("JetBrains Mono Web", see styles.css
  // @font-face) so box-drawing/block glyphs always resolve in the desktop
  // WKWebView — the system "Menlo" left them BLANK there. The distinct family
  // name guarantees the bundled woff2 is used (never a system install). Menlo
  // stays as a fallback for any glyph the bundled font lacks (e.g. braille).
  fontFamily:
    '"JetBrains Mono Web", "JuliaMono Web", "Menlo", "SF Mono", ui-monospace, "Cascadia Code", "JetBrains Mono", monospace',
  fontSize: 13,
} as const;

// Ensure the bundled terminal web font is loaded BEFORE xterm measures its cell
// size — otherwise xterm measures the fallback font's metrics and the live
// glyphs (which differ) tile wrong once the real font swaps in. Idempotent: the
// browser caches the load, so repeated calls are cheap. Best-effort: if the
// Font Loading API is unavailable (jsdom tests) it resolves immediately.
async function ensureTerminalFont(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts?.load) return;
  // Kick off the fallback font (braille/symbols) WITHOUT blocking: it only fills
  // glyphs the primary font lacks, so it never affects cell metrics — no need to
  // make every terminal mount wait on its (larger) download.
  void document.fonts.load(`${XTERM_FONT.fontSize}px "JuliaMono Web"`).catch(() => {});
  try {
    // Await the PRIMARY font (both weights) — its metrics drive cell sizing.
    await Promise.all([
      document.fonts.load(`${XTERM_FONT.fontSize}px "JetBrains Mono Web"`),
      document.fonts.load(`bold ${XTERM_FONT.fontSize}px "JetBrains Mono Web"`),
    ]);
  } catch {
    /* font load is best-effort; fall back to system metrics */
  }
}

// WebGL renderer toggle — DISABLED.
//
// xterm's `customGlyphs: true` draws box-drawing/block characters (borders,
// rules, the ▀▄█ pixel-art logos) PROCEDURALLY into a GPU texture atlas instead
// of using the font. That custom-glyph path renders CORRUPTED inside the Tauri
// desktop shell's WKWebView (WebKit): borders, horizontal rules and input
// frames fragment into broken dashes while ordinary text stays crisp (the
// "graphics not rendering on the terminal panes" report). xterm's DOM renderer
// draws those glyphs from the font instead — Menlo has full box-drawing +
// half-block coverage (see XTERM_FONT note) — so everything renders correctly,
// at the cost of GPU-accelerated repaint (negligible for an interactive
// terminal). Not loading WebglAddon leaves xterm on its default DOM renderer.
//
// Typed `boolean` (not the literal `false`) on purpose so the guard below is
// not a constant condition. Flip to true — or make this engine-conditional
// (Chromium yes / WebKit no) — to restore GPU rendering where customGlyphs is
// sound.
const ENABLE_WEBGL_RENDERER: boolean = false;

interface Props {
  activeSessionId: string | null;
  projectId?: string | null;
  rootPath?: string | null;
  visible: boolean;
  maximized: boolean;
  onClose: () => void;
  onToggleMaximized: () => void;
  /** Global enforce state from admin config. Undefined = not yet loaded. */
  defaultEnforce?: boolean;
  /** ID of the currently enlarged tile (passed down from TerminalWorkspace). */
  enlargedTileId?: string | null;
  /** Callback to enlarge/unenlarge a tile by ID. */
  onEnlargeTile?: (id: string | null) => void;
  /** Called when the user activates a file link inside a terminal tile. */
  onOpenFileRef?: (ref: ResolvedRef) => void;
}

const LAYOUT_OPTIONS: Array<[LayoutMode, string]> = [
  ["tabs", "Layout · Tabs"],
  ["grid-auto", "Layout · Auto grid"],
  ["grid-1", "Layout · 1 col"],
  ["grid-2", "Layout · 2 cols"],
  ["grid-3", "Layout · 3 cols"],
  ["grid-4", "Layout · 4 cols"],
  ["tiling", "Layout · Tiling tree"],
  ["floating", "Layout · Floating windows"],
  ["canvas", "Layout · Canvas (pan + zoom)"],
];

// How often a pane the host refuses as not this identity's looks for the
// terminal in the caller's listing again: slower than the reconnect backoff
// (which tops out at 8 s), since only switching back to the terminal's org or
// account puts it there.
export const NOT_OWNED_RETRY_MS = 15_000;

export function TerminalPane({
  activeSessionId,
  projectId,
  rootPath,
  visible,
  maximized,
  onClose,
  onToggleMaximized,
  defaultEnforce,
  enlargedTileId,
  onEnlargeTile,
  onOpenFileRef,
}: Props) {
  const state = terminalsStore.useStore();
  const workbenches = workbenchesStore.useStore();
  const wbId = workbenches.activeId || "wb-default";
  const layoutCtx = useTerminalLayout(wbId);

  // Reactive terminal list from Convex — fires whenever terminals are
  // created/closed on the server. Per [[feedback-pr46-review-patterns]] #5
  // we consume the actual Convex return shape (_id, title, status,
  // enforce, lastActiveAt) without an intermediate adapter. The mount
  // guard per [[feedback-pr47-second-pass-patterns]] #24 ensures we
  // don't dispatch into terminalsStore from a stale component.
  const convexTerminals = useQuery(convexApi.terminals.queries.listForUser, {});
  const terminalsMountedRef = useRef(true);
  useEffect(() => {
    terminalsMountedRef.current = true;
    return () => {
      terminalsMountedRef.current = false;
    };
  }, []);
  // NOTE: in this PR we subscribe to convexTerminals for the side-effect
  // of keeping the Convex socket warm + surfacing the data in React
  // devtools, but we don't yet merge it into terminalsStore — the
  // legacy /api/terminals REST list is still the source of truth and
  // gets torn out in PR 7. The prior useEffect body was a no-op
  // (`void convexTerminals`) that Gemini PR #58 flagged as dead code;
  // removed so future readers don't mistake it for wiring that exists.
  // When PR 7 removes the REST list, replace this comment block with a
  // useEffect that calls terminalsStore.setFromConvex(convexTerminals).
  const [agent, setAgent] = useState("Regular Terminal");
  const hostsRef = useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  const visibleTerminals = useMemo(
    () =>
      state.terminals.filter(
        (terminal) => (terminal.workbenchId || "wb-default") === workbenches.activeId
      ),
    [state.terminals, workbenches.activeId]
  );
  // Terminals in other workbenches stay mounted in an offstage buffer so the
  // xterm + WebSocket survive workbench switches. Without this, every switch
  // would tear down and reconnect every CLI.
  const offstageTerminals = useMemo(
    () =>
      state.terminals.filter(
        (terminal) => (terminal.workbenchId || "wb-default") !== workbenches.activeId
      ),
    [state.terminals, workbenches.activeId]
  );
  const activeTerminalId = visibleTerminals.some((terminal) => terminal.id === state.activeId)
    ? state.activeId
    : visibleTerminals[0]?.id || null;

  // Guard the enlarge state: if the enlarged terminal was closed, fall back to
  // "not enlarged" — otherwise the display:none rule below would hide EVERY
  // visible tile (the enlarged id matches none), blanking the whole pane.
  const effectiveEnlargedId =
    enlargedTileId && visibleTerminals.some((terminal) => terminal.id === enlargedTileId)
      ? enlargedTileId
      : null;

  // Reconcile the parent's enlarge state when the enlarged tile disappears
  // (closed/exited). effectiveEnlargedId already keeps the render correct, but
  // the parent's enlargedTileId would otherwise dangle on a dead id (a stale
  // ⤡/⤢ control, and a future tile reusing the slot). onEnlargeTile is the
  // parent's stable setState, so this fires once and settles (the condition is
  // false once the parent clears) — no write-back loop.
  useEffect(() => {
    if (enlargedTileId && !effectiveEnlargedId) onEnlargeTile?.(null);
  }, [enlargedTileId, effectiveEnlargedId, onEnlargeTile]);

  // Keep the layout's tiling tree in sync with the live terminal set; prune
  // floating positions for terminals that no longer exist. Without these the
  // layout would render stale tiles after a close.
  useEffect(() => {
    const ids = new Set(visibleTerminals.map((terminal) => terminal.id));
    if (layoutCtx.layout === "tiling") layoutCtx.rebuildTilingTree(ids);
    layoutCtx.pruneFloatingPositions(ids);
  }, [
    visibleTerminals,
    layoutCtx.layout,
    layoutCtx.rebuildTilingTree,
    layoutCtx.pruneFloatingPositions,
  ]);

  // ResizeObserver on the hosts container — drives tiling/canvas re-layout
  // and gives xterm.js's fit addon something to react to via the host's
  // own observer (set up inside TerminalHost).
  useEffect(() => {
    if (!hostsRef.current) return;
    const el = hostsRef.current;
    const update = () => setContainerSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  // Keep the new-terminal dropdown synced with the agent registry as agents
  // are added/removed; default to the first option if the current selection
  // has been removed.
  useEffect(() => {
    if (
      state.agents.length &&
      !state.agents.some((item) => item.name === agent) &&
      agent !== "Regular Terminal"
    ) {
      setAgent(state.agents[0].name);
    }
  }, [state.agents, agent]);

  async function spawn() {
    terminalsStore.setState({ spawning: true, error: null });
    try {
      // Match vanilla apps/web/terminal.js: measure the eventual tile via
      // a hidden xterm mount BEFORE POSTing the spawn, so the PTY (and
      // therefore the agent's TUI) is born at the same cols/rows the live
      // tile will render at. Otherwise a TUI like Claude Code paints its
      // splash at the default cols, xterm later shrinks to the real tile
      // width, and the scrollback banner wraps through the narrow viewport.
      const dims = await measureInitialDims(hostsRef.current, {
        layout: layoutCtx.layout,
        density: layoutCtx.density,
        totalTiles: visibleTerminals.length + 1,
        canvasTileW: layoutCtx.canvasTileW,
        canvasTileH: layoutCtx.canvasTileH,
      });
      const meta = await api.spawnTerminal({
        agent,
        sessionId: activeSessionId,
        workbenchId: workbenches.activeId,
        projectId: projectId || undefined,
        rootPath: rootPath || undefined,
        cols: dims.cols,
        rows: dims.rows,
      });
      terminalsStore.setState((current) => ({
        ...current,
        terminals: [...current.terminals, meta],
        activeId: meta.id,
        spawning: false,
        error: null,
      }));
    } catch (error) {
      terminalsStore.setState({
        spawning: false,
        error: error instanceof Error ? error.message : "Failed to spawn terminal",
      });
    }
  }

  async function remove(id: string) {
    await api.deleteTerminal(id).catch(() => undefined);
    terminalsStore.setState((current) => {
      // Drop any custom title for the closed terminal so it doesn't leak in
      // the store / localStorage forever (terminal ids are never reused — a
      // restart fresh-spawns new ids). Keep the same reference when there's
      // nothing to drop so the titles-persistence subscriber doesn't re-write.
      let titles = current.titles;
      if (id in titles) {
        titles = { ...titles };
        delete titles[id];
      }
      return {
        ...current,
        terminals: current.terminals.filter((terminal) => terminal.id !== id),
        activeId:
          current.activeId === id
            ? current.terminals.find((terminal) => terminal.id !== id)?.id || null
            : current.activeId,
        // Once the user has explicitly closed a terminal in this page load,
        // session.openTerminals ids are stale. The auto-reattach path skips
        // ALL prior ids when this flag is true to avoid 404 storms.
        userClosedAny: true,
        titles,
      };
    });
  }

  // ── Canvas keyboard shortcuts (Cmd/Ctrl + +/−/0) ────────────────────────
  useEffect(() => {
    if (layoutCtx.layout !== "canvas") return;
    function onKey(event: KeyboardEvent) {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      if (event.key === "=" || event.key === "+") {
        event.preventDefault();
        layoutCtx.setCanvasZoom(layoutCtx.canvasZoom + 0.1);
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        layoutCtx.setCanvasZoom(layoutCtx.canvasZoom - 0.1);
      } else if (event.key === "0") {
        event.preventDefault();
        layoutCtx.resetCanvasZoom();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [layoutCtx.layout, layoutCtx.canvasZoom, layoutCtx.setCanvasZoom, layoutCtx.resetCanvasZoom]);

  const agentNames = agentOptions(state.agents.map((profile) => profile.name));

  // ── Compute per-mode layout artifacts ───────────────────────────────────
  const layoutModel = useMemo(
    () =>
      buildLayoutModel({
        mode: layoutCtx.layout,
        terminals: visibleTerminals,
        activeTerminalId,
        containerSize,
        density: layoutCtx.density,
        tilingTree: layoutCtx.tilingTree,
        floatingPositions: layoutCtx.floatingPositions,
        canvas: {
          zoom: layoutCtx.canvasZoom,
          cols: layoutCtx.canvasCols,
          tileW: layoutCtx.canvasTileW,
          tileH: layoutCtx.canvasTileH,
        },
      }),
    [
      layoutCtx.layout,
      visibleTerminals,
      activeTerminalId,
      containerSize.width,
      containerSize.height,
      layoutCtx.density,
      layoutCtx.tilingTree,
      layoutCtx.floatingPositions,
      layoutCtx.canvasZoom,
      layoutCtx.canvasCols,
      layoutCtx.canvasTileW,
      layoutCtx.canvasTileH,
    ]
  );

  const isPositioned = ["tiling", "floating", "canvas"].includes(layoutCtx.layout);
  const isCanvas = layoutCtx.layout === "canvas";
  const showCanvasZoom = isCanvas;

  return (
    <section
      className={`terminal-pane ${maximized ? "is-maximized" : ""}`}
      hidden={!visible}
      aria-label="Embedded CLI terminals"
    >
      <header className="terminal-pane__head">
        <div className="terminal-pane__tabs" role="tablist">
          {visibleTerminals.map((terminal) => (
            <div
              key={terminal.id}
              className={`terminal-tab ${terminal.id === activeTerminalId ? "is-active" : ""} ${terminal.exited ? "is-exited" : ""}`}
              role="tab"
              tabIndex={0}
              aria-selected={terminal.id === activeTerminalId}
              onClick={() => terminalsStore.setState({ activeId: terminal.id })}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  terminalsStore.setState({ activeId: terminal.id });
                }
              }}
            >
              <span className="terminal-tab__dot" />
              <span className="terminal-tab__label">
                {state.titles[terminal.id] || terminal.agent}
              </span>
              {onEnlargeTile && (
                <button
                  type="button"
                  className="tw__tile__btn"
                  aria-label={enlargedTileId === terminal.id ? "Restore tile size" : "Enlarge tile"}
                  title={enlargedTileId === terminal.id ? "Restore" : "Enlarge"}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEnlargeTile(enlargedTileId === terminal.id ? null : terminal.id);
                  }}
                >
                  {enlargedTileId === terminal.id ? "⤡" : "⤢"}
                </button>
              )}
              <button
                className="terminal-tab__close"
                type="button"
                aria-label={`Close ${terminal.agent} terminal`}
                onClick={(event) => {
                  event.stopPropagation();
                  void remove(terminal.id);
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="terminal-pane__newtab">
          <TwSelect
            value={agent}
            options={agentNames.map((name) => ({ value: name, label: name }))}
            onChange={setAgent}
            ariaLabel="Agent for new terminal"
          />
          <button
            type="button"
            className="tw-newbtn"
            onClick={spawn}
            disabled={state.spawning}
            title="Start a new terminal with the selected agent"
          >
            <Plus size={14} aria-hidden="true" />
            <span>{state.spawning ? "Starting…" : "New"}</span>
          </button>
        </div>
        <div className="terminal-pane__actions">
          <PolicyChip defaultEnforce={defaultEnforce} />
          <TwSelect
            value={layoutCtx.layout}
            options={LAYOUT_OPTIONS.map(([value, label]) => ({ value, label }))}
            onChange={(next) => layoutCtx.setLayout(next as LayoutMode)}
            ariaLabel="Terminal layout"
            title="How to arrange open terminals"
          />
          {layoutCtx.layout === "grid-auto" && (
            <label
              className="terminal-pane__density"
              title="Minimum width per tile in the auto grid."
            >
              <span aria-hidden="true">⇔</span>
              <input
                type="range"
                min="320"
                max="900"
                step="20"
                value={layoutCtx.density}
                aria-label="Tile width"
                onChange={(event) => layoutCtx.setDensity(Number(event.target.value))}
              />
            </label>
          )}
          {showCanvasZoom && (
            <div className="terminal-pane__zoom">
              <button
                type="button"
                className="button secondary"
                aria-label="Zoom out"
                title="Zoom out (Cmd/Ctrl + −)"
                onClick={() => layoutCtx.setCanvasZoom(layoutCtx.canvasZoom - 0.1)}
              >
                −
              </button>
              <span
                className="terminal-pane__zoom-readout"
                title="Click to reset zoom to 100%"
                onClick={layoutCtx.resetCanvasZoom}
              >
                {Math.round(layoutCtx.canvasZoom * 100)}%
              </span>
              <button
                type="button"
                className="button secondary"
                aria-label="Zoom in"
                title="Zoom in (Cmd/Ctrl + +)"
                onClick={() => layoutCtx.setCanvasZoom(layoutCtx.canvasZoom + 0.1)}
              >
                +
              </button>
              <button
                type="button"
                className="button secondary"
                title="Fit all tiles to the visible area"
                onClick={() =>
                  layoutCtx.fitCanvasZoom(
                    containerSize.width,
                    containerSize.height,
                    visibleTerminals.length
                  )
                }
              >
                Fit
              </button>
            </div>
          )}
          <button
            type="button"
            className="tw-iconbtn"
            onClick={onToggleMaximized}
            title={maximized ? "Restore terminal pane" : "Maximize terminal pane"}
            aria-label={maximized ? "Restore terminal pane" : "Maximize terminal pane"}
          >
            {maximized ? (
              <Minimize2 size={15} aria-hidden="true" />
            ) : (
              <Maximize2 size={15} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className="tw-iconbtn"
            title="Hide the terminal pane (keeps running terminals)"
            aria-label="Hide terminal pane"
            onClick={onClose}
          >
            <X size={15} aria-hidden="true" />
          </button>
        </div>
      </header>
      {state.error && <div className="terminal-pane__host-placeholder">{state.error}</div>}

      <div
        ref={hostsRef}
        className={`terminal-pane__hosts ${layoutCtx.layout === "floating" ? "is-floating" : ""} ${effectiveEnlargedId ? "is-enlarged" : ""}`}
        data-layout={layoutCtx.layout}
        style={layoutModel.hostsContainerStyle}
      >
        {layoutCtx.layout === "canvas" ? (
          <div className="terminal-pane__canvas-inner" style={layoutModel.canvasInnerStyle}>
            {visibleTerminals.map((terminal) => (
              <TerminalTile
                key={terminal.id}
                terminal={terminal}
                position={layoutModel.tilePositions[terminal.id]}
                isActive={terminal.id === activeTerminalId || isPositioned}
                positioned
                hiddenByEnlarge={Boolean(
                  effectiveEnlargedId && effectiveEnlargedId !== terminal.id
                )}
                title={state.titles[terminal.id] || terminal.agent}
                onRename={(next) => setTerminalTitle(terminal.id, next)}
                isEnlarged={enlargedTileId === terminal.id}
                onEnlarge={
                  onEnlargeTile
                    ? () => onEnlargeTile(enlargedTileId === terminal.id ? null : terminal.id)
                    : undefined
                }
                onClose={() => void remove(terminal.id)}
                onOpenFileRef={onOpenFileRef}
                resolveCtx={{ projectId: projectId ?? null, rootPath: rootPath ?? null }}
              />
            ))}
          </div>
        ) : (
          <>
            {visibleTerminals.map((terminal) => (
              <TerminalTile
                key={terminal.id}
                terminal={terminal}
                position={layoutModel.tilePositions[terminal.id]}
                isActive={terminal.id === activeTerminalId || layoutCtx.layout.startsWith("grid")}
                positioned={isPositioned}
                floatingTitlebar={layoutCtx.layout === "floating"}
                hiddenByEnlarge={Boolean(
                  effectiveEnlargedId && effectiveEnlargedId !== terminal.id
                )}
                title={state.titles[terminal.id] || terminal.agent}
                onRename={(next) => setTerminalTitle(terminal.id, next)}
                isEnlarged={enlargedTileId === terminal.id}
                onEnlarge={
                  onEnlargeTile
                    ? () => onEnlargeTile(enlargedTileId === terminal.id ? null : terminal.id)
                    : undefined
                }
                onClose={() => void remove(terminal.id)}
                onFloatingMove={
                  layoutCtx.layout === "floating"
                    ? (pos) => layoutCtx.setFloatingPosition(terminal.id, pos)
                    : undefined
                }
                onRaise={
                  layoutCtx.layout === "floating"
                    ? () => layoutCtx.raiseFloating(terminal.id)
                    : undefined
                }
                onOpenFileRef={onOpenFileRef}
                resolveCtx={{ projectId: projectId ?? null, rootPath: rootPath ?? null }}
              />
            ))}

            {layoutCtx.layout === "tiling" &&
              !effectiveEnlargedId &&
              layoutModel.tilingSplits.map((split, index) => (
                <TilingSplitterEl
                  key={split.path.join(",") || `root-${index}`}
                  split={split}
                  onChange={(ratio) => layoutCtx.setTilingRatio(split.path, ratio)}
                />
              ))}
          </>
        )}

        {!visibleTerminals.length && (
          <div className="terminal-pane__host is-active">
            <div className="terminal-pane__host-placeholder">
              Start a terminal to attach a guarded CLI.
            </div>
          </div>
        )}
      </div>

      {/*
        Offstage buffer: terminals belonging to OTHER workbenches stay
        mounted here so their xterm + WebSocket survive a workbench
        switch. Positioned far off-screen with a real size so xterm.fit()
        keeps producing valid dimensions and the agent's output still
        renders correctly the next time the workbench is selected.
      */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: -99999,
          top: 0,
          width: 800,
          height: 480,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {offstageTerminals.map((terminal) => (
          <div
            key={terminal.id}
            className="terminal-pane__host"
            style={{ width: "100%", height: "100%" }}
          >
            <TerminalHost
              terminal={terminal}
              onOpenFileRef={onOpenFileRef}
              resolveCtx={{ projectId: projectId ?? null, rootPath: rootPath ?? null }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Layout model: converts mode + state into renderable positions ────────────

interface LayoutModel {
  hostsContainerStyle: CSSProperties;
  canvasInnerStyle: CSSProperties;
  tilePositions: Record<string, PositionedTile | undefined>;
  tilingSplits: TilingSplitter[];
}

interface BuildModelArgs {
  mode: LayoutMode;
  terminals: TerminalMeta[];
  activeTerminalId: string | null;
  containerSize: { width: number; height: number };
  density: number;
  tilingTree: ReturnType<typeof useTerminalLayout>["tilingTree"];
  floatingPositions: Record<string, FloatingPosition>;
  canvas: { zoom: number; cols: number; tileW: number; tileH: number };
}

function buildLayoutModel(args: BuildModelArgs): LayoutModel {
  const { mode, terminals, containerSize, density, tilingTree, floatingPositions, canvas } = args;
  const tilePositions: Record<string, PositionedTile | undefined> = {};
  const tilingSplits: TilingSplitter[] = [];

  if (mode === "tiling" && tilingTree && containerSize.width && containerSize.height) {
    const rect = {
      left: 10,
      top: 10,
      width: containerSize.width - 20,
      height: containerSize.height - 20,
    };
    const out = { tiles: [] as PositionedTile[], splits: [] as TilingSplitter[] };
    layoutTilingTree(tilingTree, rect, out);
    for (const tile of out.tiles) tilePositions[tile.id] = tile;
    tilingSplits.push(...out.splits);
  } else if (mode === "floating") {
    const cw = containerSize.width || 1000;
    const ch = containerSize.height || 700;
    const defaultW = Math.min(540, Math.max(360, Math.round(cw * 0.45)));
    const defaultH = Math.min(420, Math.max(280, Math.round(ch * 0.5)));
    let cascade = Object.keys(floatingPositions).length;
    let nextZ = 10 + Math.max(0, ...Object.values(floatingPositions).map((p) => p.z || 0));
    terminals.forEach((terminal) => {
      const existing = floatingPositions[terminal.id];
      if (existing) {
        tilePositions[terminal.id] = {
          id: terminal.id,
          left: existing.left,
          top: existing.top,
          width: existing.width,
          height: existing.height,
          z: existing.z,
        };
      } else {
        const step = cascade % 10;
        tilePositions[terminal.id] = {
          id: terminal.id,
          left: 30 + step * 28,
          top: 30 + step * 24,
          width: defaultW,
          height: defaultH,
          z: nextZ++,
        };
        cascade++;
      }
    });
  } else if (mode === "canvas") {
    const { tileW, tileH, cols } = canvas;
    const gap = 14,
      pad = 18;
    terminals.forEach((terminal, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      tilePositions[terminal.id] = {
        id: terminal.id,
        left: pad + col * (tileW + gap),
        top: pad + row * (tileH + gap),
        width: tileW,
        height: tileH,
      };
    });
  }

  // ── Container/inner styles ──────────────────────────────────────────────
  let hostsContainerStyle: CSSProperties = {};
  if (mode === "grid-auto") {
    hostsContainerStyle = { gridTemplateColumns: `repeat(auto-fit, minmax(${density}px, 1fr))` };
  } else if (mode.startsWith("grid-")) {
    const cols = parseInt(mode.replace("grid-", ""), 10) || 1;
    hostsContainerStyle = { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` };
  } else if (mode === "tiling" || mode === "floating") {
    hostsContainerStyle = { display: "block", position: "relative" };
  } else if (mode === "canvas") {
    hostsContainerStyle = { overflow: "auto", display: "block", position: "relative" };
  }

  let canvasInnerStyle: CSSProperties = {};
  if (mode === "canvas") {
    const { tileW, tileH, cols, zoom } = canvas;
    const gap = 14,
      pad = 18;
    const rows = Math.max(1, Math.ceil(terminals.length / cols));
    const totalW = pad * 2 + cols * tileW + (cols - 1) * gap;
    const totalH = pad * 2 + rows * tileH + (rows - 1) * gap;
    canvasInnerStyle = {
      width: `${totalW}px`,
      height: `${totalH}px`,
      transform: `scale(${zoom})`,
      transformOrigin: "0 0",
      marginRight: `${totalW * (zoom - 1)}px`,
      marginBottom: `${totalH * (zoom - 1)}px`,
      position: "relative",
    };
  }

  return { hostsContainerStyle, canvasInnerStyle, tilePositions, tilingSplits };
}

// ── Tile renderer ───────────────────────────────────────────────────────────

interface TerminalTileProps {
  terminal: TerminalMeta;
  position?: PositionedTile;
  isActive: boolean;
  positioned: boolean;
  floatingTitlebar?: boolean;
  onFloatingMove?: (next: FloatingPosition) => void;
  onRaise?: () => void;
  /** Resolved display title (user override, else the agent name). */
  title: string;
  /** Persist a rename (empty string clears the override). */
  onRename: (next: string) => void;
  /** Toggle enlarge for this tile. */
  onEnlarge?: () => void;
  /** Close this terminal. */
  onClose?: () => void;
  isEnlarged?: boolean;
  hiddenByEnlarge?: boolean;
  /** Called when the user activates a file link inside this tile. */
  onOpenFileRef?: (ref: ResolvedRef) => void;
  /** Context for resolving file paths (projectId + rootPath). */
  resolveCtx?: ResolveCtx;
}

function TerminalTile({
  terminal,
  position,
  isActive,
  positioned,
  floatingTitlebar,
  onFloatingMove,
  onRaise,
  title,
  onRename,
  onEnlarge,
  onClose,
  isEnlarged,
  hiddenByEnlarge,
  onOpenFileRef,
  resolveCtx,
}: TerminalTileProps) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  // Single commit path: onBlur is the ONLY place a rename is saved. Enter and
  // Escape both blur the input rather than saving/cancelling inline, so the
  // commit runs exactly once — while the element is still focused. By the time
  // React unmounts the input it is no longer focused, so the well-known
  // blur-on-unmount does NOT fire a second, stale save. `cancelEditRef` lets
  // Escape reuse the same blur path while suppressing the save.
  const cancelEditRef = useRef(false);
  function commitTitle() {
    setEditingTitle(false);
    if (cancelEditRef.current) {
      cancelEditRef.current = false;
      return;
    }
    if (titleDraft.trim() !== title) onRename(titleDraft);
  }
  const style: CSSProperties =
    positioned && position
      ? {
          position: "absolute",
          left: position.left,
          top: position.top,
          width: position.width,
          height: position.height,
          zIndex: position.z,
        }
      : {};
  if (hiddenByEnlarge) style.display = "none";

  function onDragTitleBar(event: ReactPointerEvent<HTMLDivElement>) {
    if (!onFloatingMove || !position) return;
    if (event.button !== 0) return;
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startLeft = position.left;
    const startTop = position.top;
    const handle = event.currentTarget;
    handle.classList.add("is-dragging");
    const onMove = (ev: PointerEvent) => {
      onFloatingMove({
        left: startLeft + (ev.clientX - startX),
        top: Math.max(0, startTop + (ev.clientY - startY)),
        width: position.width,
        height: position.height,
        z: position.z || 1,
      });
    };
    const onUp = () => {
      handle.classList.remove("is-dragging");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  function onDragResize(event: ReactPointerEvent<HTMLDivElement>) {
    if (!onFloatingMove || !position) return;
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = position.width;
    const startH = position.height;
    const onMove = (ev: PointerEvent) => {
      onFloatingMove({
        left: position.left,
        top: position.top,
        width: Math.max(240, startW + (ev.clientX - startX)),
        height: Math.max(180, startH + (ev.clientY - startY)),
        z: position.z || 1,
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  return (
    <div
      className={`terminal-pane__host ${isActive ? "is-active" : ""}`}
      data-tile-id={terminal.id}
      style={style}
      aria-hidden={hiddenByEnlarge || undefined}
      onPointerDown={onRaise}
    >
      {/* Per-tile header — mirrors the chat tile: renameable title + enlarge +
          close. In floating mode it also serves as the window drag handle
          (the float-titlebar it replaces). */}
      <div
        className="terminal-pane__tile-header"
        onPointerDown={floatingTitlebar ? onDragTitleBar : undefined}
      >
        {editingTitle ? (
          <input
            className="terminal-pane__tile-title-input"
            autoFocus
            value={titleDraft}
            aria-label="Terminal name"
            onPointerDown={(e) => e.stopPropagation()}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelEditRef.current = true;
                e.currentTarget.blur();
              }
            }}
          />
        ) : (
          <span
            className="terminal-pane__tile-title"
            title="Double-click to rename"
            onDoubleClick={() => {
              cancelEditRef.current = false;
              setTitleDraft(title);
              setEditingTitle(true);
            }}
          >
            {title}
          </span>
        )}
        <div className="terminal-pane__tile-actions">
          {onEnlarge && (
            <button
              type="button"
              className="terminal-pane__tile-btn"
              aria-label={isEnlarged ? "Restore terminal size" : "Enlarge terminal"}
              title={isEnlarged ? "Restore" : "Enlarge"}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onEnlarge();
              }}
            >
              {isEnlarged ? "⤡" : "⤢"}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="terminal-pane__tile-btn"
              aria-label={`Close ${title} terminal`}
              title="Close terminal"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>
      <div className="terminal-pane__tile-body">
        {/* nudge carries the tile's committed pixel size in positioned modes
            (tiling/floating/canvas) so the host re-fits the PTY after a
            splitter drag; undefined in grid/tabs, where the ResizeObserver
            alone already tracks the CSS-driven size correctly. */}
        <TerminalHost
          terminal={terminal}
          nudge={
            position ? `${Math.round(position.width)}x${Math.round(position.height)}` : undefined
          }
          isEnlarged={isEnlarged}
          onOpenFileRef={onOpenFileRef}
          resolveCtx={resolveCtx}
        />
      </div>
      {floatingTitlebar && (
        <div className="terminal-pane__float-resize" onPointerDown={onDragResize} />
      )}
    </div>
  );
}

function TilingSplitterEl({
  split,
  onChange,
}: {
  split: TilingSplitter;
  onChange: (ratio: number) => void;
}) {
  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    const startPos = split.dir === "v" ? event.clientX : event.clientY;
    const startRatio = split.ratio;
    const target = event.currentTarget;
    target.classList.add("is-dragging");
    document.body.style.cursor = split.dir === "v" ? "col-resize" : "row-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev: PointerEvent) => {
      const pos = split.dir === "v" ? ev.clientX : ev.clientY;
      const dRatio = (pos - startPos) / split.containerSize;
      onChange(startRatio + dRatio);
    };
    const onUp = () => {
      target.classList.remove("is-dragging");
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  return (
    <div
      className={`terminal-pane__tiling-splitter is-${split.dir}`}
      style={{
        position: "absolute",
        left: split.left,
        top: split.top,
        width: split.width,
        height: split.height,
      }}
      onPointerDown={onPointerDown}
      onDoubleClick={() => onChange(0.5)}
    />
  );
}

// Maximum characters shown in the floating ghost chip during a drag — long
// selections are truncated to keep the chip readable.
const GHOST_MAX_CHARS = 40;

// Resolve which terminal is under the drop point and paste into it. Exported
// under a test-only alias because the pixel hit-test of the selection (the
// drag START) can't run in jsdom, but the drop ROUTING is deterministic.
function dropDraggedText(text: string, x: number, y: number): void {
  const el = document.elementFromPoint(x, y);
  const hostEl = el?.closest("[data-terminal-id]") as HTMLElement | null;
  const id = hostEl?.dataset.terminalId;
  if (id) pasteIntoTerminal(id, text);
}
export const __dropDraggedText__ /* @internal — test-only */ = dropDraggedText;

function TerminalHost({
  terminal,
  nudge,
  isEnlarged,
  onOpenFileRef,
  resolveCtx,
}: {
  terminal: TerminalMeta;
  nudge?: string;
  isEnlarged?: boolean;
  onOpenFileRef?: (ref: ResolvedRef) => void;
  resolveCtx?: ResolveCtx;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  // A held PTY egress `ask` (set by the `egress-approval` WS message, cleared by
  // `egress-approval-resolved`, `egress-approval-none` (what the host says when
  // a socket attaches and nothing is held), an `exit`, or a successful
  // decision). When set, the inline approve/deny card renders over this
  // terminal.
  const [egressApproval, setEgressApproval] = useState<{
    approvalId: string;
    host: string;
    command: string;
    reason: string;
  } | null>(null);
  // What the person who clicked the card should know when the host answered
  // the agent with a decision recorded elsewhere instead of theirs. The card
  // unmounts as soon as the ask resolves, so the notice lives here.
  const [egressNotice, setEgressNotice] = useState<string | null>(null);
  // The ask the socket raised last, or null once the terminal exits. Set as
  // each message arrives, not at render, so a decision whose request returns
  // after a newer ask or an exit sees it even before React has rendered it.
  const latestEgressAskRef = useRef<string | null>(null);

  // Resolve the held egress ask via the Bun-host decide route. For an ask with
  // a Convex row the host records the decision there first and answers the
  // agent with the decision that stands: a 409 with `decision` means one was
  // already recorded elsewhere and the agent got that. An ask without a row is
  // answered directly (terminalEngine.resolveEgressApproval writes y/n on the
  // held PTY). The decide route requires a non-empty note on a deny, so
  // default one.
  const decideEgress = useCallback(
    async (decision: "approved" | "denied", note: string) => {
      if (!egressApproval) return;
      const { approvalId } = egressApproval;
      const res = await clerkFetch(`/api/approvals/${encodeURIComponent(approvalId)}/decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          decision,
          note: decision === "denied" ? note || "Denied from the terminal" : note,
        }),
      });
      let notice: string | null = null;
      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as {
          error?: string;
          decision?: string;
        } | null;
        const overruled =
          res.status === 409 && (detail?.decision === "approved" || detail?.decision === "denied");
        if (!overruled) throw new Error(detail?.error || `Decision failed (${res.status}).`);
        notice = detail?.error || "This request was already decided elsewhere.";
      }
      // A newer ask, or an exit, arrived while this request was out: the card
      // it was made from is already replaced or closed, and its notice would
      // describe a request no longer on screen.
      if (latestEgressAskRef.current !== approvalId) return;
      // Optimistic clear (the `egress-approval-resolved` WS message also clears
      // it, on every attached client).
      setEgressApproval((cur) => (cur && cur.approvalId === approvalId ? null : cur));
      if (notice) setEgressNotice(notice);
    },
    [egressApproval]
  );
  // getToken is stable across renders; keep it in a ref so the connect effect
  // (deps: [terminal.id]) can read the current one without re-running on every
  // auth refresh. Used to mint the Clerk "convex"-template JWT for the WS gate.
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  // Same pattern for the file-link resolve context: the connect effect registers
  // the link provider once (deps: [terminal.id]), but a project/root switch must
  // take effect on the next hover. Keep the latest ctx in a ref the provider's
  // getCtx reads lazily, instead of capturing a snapshot at mount (Codex C2).
  const resolveCtxRef = useRef(resolveCtx);
  resolveCtxRef.current = resolveCtx;
  // Imperative "re-fit + push resize" handle. attach() points it at the live
  // sendResize once the terminal + fit addon exist; the nudge effect below
  // calls it after a layout-driven size change. Needed because the host's own
  // ResizeObserver fires DURING a React-state-driven pane resize (tiling /
  // floating / canvas splitter drag), racing React's commit/paint — so its
  // debounced send can measure a transient mid-drag size and never deliver the
  // final settled cols/rows to the PTY. A whole-window OS resize has no such
  // race, which is why only in-app pane resizes lost the resolution.
  const refitRef = useRef<(() => void) | null>(null);
  // Live xterm instance, exposed so the enlarge effect can restore keyboard
  // focus: clicking the ⤢ button blurs the terminal's hidden textarea, so after
  // enlarging you "can't type" until something refocuses it.
  const termRef = useRef<import("@xterm/xterm").Terminal | null>(null);
  // WebGL addon instance + the dynamically-imported module. WebGL is the
  // default renderer; the module ref is kept so the BiDi auto-switch can dispose
  // the addon (live-restoring xterm's DOM renderer, which does BiDi) the first
  // time RTL text appears in this terminal.
  const webglRef = useRef<import("@xterm/addon-webgl").WebglAddon | null>(null);
  const webglModRef = useRef<typeof import("@xterm/addon-webgl") | null>(null);
  const bidiActiveRef = useRef(false);
  // Mirror isEnlarged into a ref so attach() — which runs async, after the
  // dynamic xterm import resolves — can focus a tile that mounted ALREADY
  // enlarged (e.g. enlargedId restored from a persisted layout). The
  // [isEnlarged] effect below cannot: on first mount it fires during the import
  // gap while termRef.current is still null, so its focus call no-ops and the
  // enlarged tile "can't be typed into" until the user clicks it.
  const isEnlargedRef = useRef(isEnlarged);
  isEnlargedRef.current = isEnlarged;

  // Drop to the DOM renderer (which the browser BiDi-reorders) + flag the host
  // so the unicode-bidi CSS applies. Idempotent. Disposing the WebGL addon
  // restores xterm's default DOM renderer and triggers a full repaint, so any
  // already-painted backwards Hebrew is re-rendered correctly.
  const enterBidiMode = useCallback(() => {
    if (bidiActiveRef.current) return;
    bidiActiveRef.current = true;
    hostRef.current?.classList.add("agp-rtl");
    try {
      webglRef.current?.dispose();
    } catch {
      /* already disposed / never loaded */
    }
    webglRef.current = null;
  }, []);

  // ── Right-click Copy/Paste context menu ─────────────────────────────────
  // position:fixed coords from the contextmenu event, plus a boolean that
  // reflects whether xterm has an active selection at the moment of right-click
  // (so Copy is disabled when there is nothing to copy).
  const [menu, setMenu] = useState<{ x: number; y: number; canCopy: boolean } | null>(null);

  const doCopy = useCallback(() => {
    const term = termRef.current;
    if (term?.hasSelection()) void clipboardWriteText(term.getSelection());
  }, []);

  const closeMenu = useCallback(() => setMenu(null), []);

  const doPaste = useCallback(() => {
    const term = termRef.current;
    if (!term) return;
    // clipboardReadText() uses the Tauri clipboard-manager plugin in the
    // desktop build (navigator.clipboard.readText is blocked in wry) and
    // returns "" rather than throwing when the clipboard is unreadable.
    void clipboardReadText().then((text) => {
      if (text) term.paste(text);
    });
  }, []);

  useEffect(() => {
    let disposed = false;
    let cleanup: () => void = () => {};
    let fileLinksDispose: (() => void) | null = null;

    async function attach() {
      if (!hostRef.current) return;
      // unicode-graphemes + webgl are xterm 6's stable successors to the
      // deprecated unicode11 + canvas addons. Both are loaded dynamically so
      // a missing build (e.g. headless test, restricted browser) degrades to
      // the built-in Unicode 6 width tables and DOM renderer instead of
      // failing the whole terminal mount.
      const [{ Terminal }, { FitAddon }, graphemesMod, webglMod] = await Promise.all([
        import("@xterm/xterm"),
        import("@xterm/addon-fit"),
        import("@xterm/addon-unicode-graphemes").catch(() => null),
        import("@xterm/addon-webgl").catch(() => null),
      ]);
      if (disposed || !hostRef.current) return;
      // Wait for the bundled web font before measuring — see ensureTerminalFont.
      await ensureTerminalFont();
      if (disposed || !hostRef.current) return;

      const term = new Terminal({
        // 1.0 keeps cell height equal to the font's natural glyph height,
        // which is what half-block characters ▀ ▄ █ used in pixel-art logos
        // (Claude Code, etc) need to butt together seamlessly.
        ...XTERM_FONT,
        lineHeight: 1.0,
        letterSpacing: 0,
        fontWeight: 400,
        fontWeightBold: 700,
        // WKWebView clips xterm's custom block/box glyph path; let the bundled
        // terminal fonts draw those characters instead.
        customGlyphs: false,
        // rescaleOverlappingGlyphs auto-shrinks any glyph that overflows
        // its cell so it stops bleeding into neighbours.
        rescaleOverlappingGlyphs: true,
        cursorBlink: true,
        scrollback: 5000,
        // Required so the unicode-graphemes addon can register itself via
        // term.unicode.register and switch term.unicode.activeVersion.
        allowProposedApi: true,
        // Agent TUIs (Claude Code, etc.) enable terminal mouse-reporting, which
        // routes mouse drags to the app so you CAN'T highlight text to copy.
        // This lets the user hold Option (⌥) and drag to force a normal xterm
        // selection even while mouse mode is active — the standard escape hatch
        // (iTerm/Terminal.app do the same). Plain shells (no mouse mode) still
        // select with a normal drag.
        macOptionClickForcesSelection: true,
        windowOptions: {
          getCellSizePixels: true,
          getWinSizeChars: true,
          getWinSizePixels: true,
        },
        theme: getXtermTheme(),
        // Standard ANSI palettes are tuned per-background and several slots
        // (yellows, greens, dim white) sit near 2:1 against the opposite
        // background. Let xterm auto-adjust any foreground that falls below
        // WCAG 4.5:1 at render time — same enforcement (and same value) VS
        // Code ships by default — instead of hand-tuning 16 colors per theme
        // (Codex review round 2, PR #253).
        minimumContrastRatio: 4.5,
      });
      termRef.current = term;
      // Keep this terminal's palette in sync with app-wide light/dark changes
      // that happen after it's already open — xterm reads theme once at
      // construction and otherwise never revisits it. See theme.ts for why
      // this needs a MutationObserver rather than a plain event listener.
      const unsubscribeTheme = subscribeThemeChange((next) => {
        term.options.theme = getXtermTheme(next);
      });
      // Register this terminal's paste fn so the drag layer can deliver dropped
      // text without prop-drilling. Unregistered in cleanup() below.
      // Closure over `term` is safe: this attach effect runs once per terminal.id
      // (single xterm instance per mount), so `term` is always the live instance.
      const unregisterPaste = registerTerminalPaste(terminal.id, (text) => term.paste(text));

      // Clipboard copy. xterm draws its selection to a canvas (not the DOM),
      // so the browser's native Cmd+C can't see it — wire it explicitly. Only
      // consume Cmd+C when there's a selection; Ctrl+C is a different key and
      // still reaches the PTY as interrupt.
      //
      // Cmd+V is intentionally NOT intercepted: xterm's own native paste (via
      // the textarea paste event) works in wry once the remote-IPC capability
      // is granted, and intercepting it here too made it paste twice. The
      // right-click "Paste" menu item (which has no native paste event) is the
      // one that routes through the clipboard-manager plugin — see doPaste.
      term.attachCustomKeyEventHandler((event) => {
        if (event.type !== "keydown") return true;
        const meta = event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey;
        if (meta && event.key === "c" && term.hasSelection()) {
          void clipboardWriteText(term.getSelection());
          return false;
        }
        return true;
      });

      const fit = new FitAddon();
      term.loadAddon(fit);
      // Unicode graphemes BEFORE term.open() so width calculations are
      // correct from the first paint. Fixes cell-width overdraw on emoji,
      // ZWJ sequences, ✻ ❯ ⏺ braille spinner glyphs, and box-drawing chars
      // whose East-Asian Width changed between Unicode 6 (xterm.js default)
      // and the modern Unicode database.
      if (graphemesMod && graphemesMod.UnicodeGraphemesAddon) {
        try {
          term.loadAddon(new graphemesMod.UnicodeGraphemesAddon());
          term.unicode.activeVersion = "15-graphemes";
        } catch (err) {
          console.warn("agentguard-terminal: UnicodeGraphemesAddon failed to load", err);
        }
      }
      term.open(hostRef.current);
      // WebGL addon would be loaded AFTER term.open() (it attaches to the host
      // element xterm just mounted, replacing the DOM renderer with a GPU one
      // and activating customGlyphs). It is currently DISABLED via
      // ENABLE_WEBGL_RENDERER — the customGlyphs box-drawing path renders
      // corrupted in the desktop WKWebView; see that constant's note. xterm
      // stays on its DOM renderer, which draws box-drawing from the font and
      // also handles BiDi natively, so the auto-switch below becomes a no-op
      // (webglRef stays null → enterBidiMode's dispose() is a harmless no-op).
      webglModRef.current = webglMod;
      if (ENABLE_WEBGL_RENDERER && webglMod && webglMod.WebglAddon) {
        try {
          const webgl = new webglMod.WebglAddon();
          term.loadAddon(webgl);
          webglRef.current = webgl;
        } catch (err) {
          console.warn("agentguard-terminal: WebglAddon failed to load", err);
        }
      }
      fit.fit();
      // If this tile mounted already enlarged, focus it now that the terminal
      // exists — the [isEnlarged] effect below ran during the async-import gap
      // with termRef.current still null and couldn't. Without this, an
      // already-enlarged tile can't be typed into until the user clicks it.
      if (isEnlargedRef.current) term.focus();

      // Register the file-path link provider (hover underline + double-click
      // to open). Only active when the parent passed both callbacks. The
      // disposer is stored in the effect-scoped `fileLinksDispose` so the
      // cleanup path can call it alongside the other teardown.
      if (onOpenFileRef && resolveCtx) {
        fileLinksDispose = registerFileLinkProvider(term, {
          getCtx: () => resolveCtxRef.current ?? { projectId: null, rootPath: null },
          onOpenFileRef,
        });
      }

      // Replay the previous transcript BEFORE the WebSocket connects so the
      // xterm shows the agent's prior visible output (ANSI colors, cursor
      // positions intact). The terminalsStore snapshot is read directly so
      // we don't have to thread the replay payload through props.
      const replay = terminalsStore.getSnapshot().replayScrollback[terminal.id];
      if (replay && containsRtl(replay)) enterBidiMode();
      if (replay) {
        term.write(replay);
        term.write("\r\n\x1b[2m── reattached after restart ──\x1b[0m\r\n");
        // One-shot — clear the replay so a future remount doesn't re-paint it.
        terminalsStore.setState((current) => {
          if (!current.replayScrollback[terminal.id]) return current;
          const next = { ...current.replayScrollback };
          delete next[terminal.id];
          return { ...current, replayScrollback: next };
        });
      } else if (terminal.reattached) {
        // Server-side reattach (via tmux/daemon): the agent's tmux session
        // paints its current screen state as soon as we attach, no replay
        // needed. Add a marker line so the user knows what happened.
        term.write(
          `\x1b[2m── reattaching to ${terminal.agent} (${terminal.id.replace(/^term-/, "").slice(0, 8)}) ──\x1b[0m\r\n`
        );
      }

      // --- WebSocket transport with auto-reconnect ---------------------------
      // The host can drop under us two ways: a transient WS loss (sleep/wake,
      // network blip) while the host process stays alive, or a full host
      // RESTART (crash + relaunch). The PTY/tmux session survives server-side
      // either way, but a restart leaves the host's in-memory terminal map
      // empty, so the /io upgrade 404s until the session is re-adopted with a
      // POST {reattachId} — a bare WS reopen would loop forever. So a reconnect
      // whose socket never opened reattaches first (see connect()), then the
      // host repaints its current screen. Without any reconnect, a drop left
      // this socket closed forever and SILENTLY broke typing until a full app
      // relaunch. `ws` is reassigned per connect; the input/resize wiring below
      // reads the live `ws` so it keeps working across reconnects.
      let ws: WebSocket | null = null;
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      // Post-open re-fit batch (a rAF + staggered setTimeouts) scheduled in the
      // socket "open" handler below. Tracked so cleanup() (unmount) and each new
      // open (reconnect) can cancel a still-pending batch. Otherwise they fire
      // after teardown — the visibility guard makes that a no-op TODAY, but that
      // leans on React having already nulled hostRef.current, and the closures
      // pin term/fit/ws until they fire (a bounded leak that also accumulates
      // across reconnects).
      let openRaf: ReturnType<typeof requestAnimationFrame> | null = null;
      const openTimers: ReturnType<typeof setTimeout>[] = [];
      let reconnectAttempts = 0;
      // Set when the host reports the PTY process exited on its own. A normal
      // exit must NOT reconnect — reattaching would respawn a fresh shell for a
      // terminal the user deliberately ended.
      let hasExited = false;
      // Set while the host refuses the terminal as not this identity's (see
      // connect()), so its notice is written once per detachment.
      let detached = false;
      // The terminal is over (its process exited, or the host refused to
      // restore it): a held ask is moot, no socket will ever resolve it, and a
      // decision that comes back later has nothing left to report on.
      const endEgress = () => {
        latestEgressAskRef.current = null;
        setEgressApproval(null);
        setEgressNotice(null);
      };

      const handleMessage = (event: MessageEvent) => {
        let payload: Record<string, unknown>;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        if (payload.type === "data" && typeof payload.data === "string") {
          // The first RTL chunk flips this terminal into BiDi (one-way — once on
          // the DOM renderer it stays there for the terminal's life). A codepoint
          // split across two WS frames is detected on the next complete char
          // (Hebrew never appears as a lone isolated char), so at most one chunk's
          // delay — acceptable. enterBidiMode is idempotent via bidiActiveRef.
          if (!bidiActiveRef.current && containsRtl(payload.data)) enterBidiMode();
          term.write(payload.data);
        }
        if (payload.type === "exit") {
          hasExited = true;
          term.writeln(`\r\n[exited ${payload.exitCode ?? ""}]`);
          endEgress();
        }
        if (payload.type === "egress-approval" && typeof payload.approvalId === "string") {
          // Only a different request retires the notice. A reconnect re-sends
          // the request still held, which can land after the click's answer.
          if (payload.approvalId !== latestEgressAskRef.current) setEgressNotice(null);
          latestEgressAskRef.current = payload.approvalId;
          setEgressApproval({
            approvalId: payload.approvalId,
            host: typeof payload.host === "string" ? payload.host : "",
            command: typeof payload.command === "string" ? payload.command : "",
            reason: typeof payload.reason === "string" ? payload.reason : "",
          });
        }
        if (payload.type === "egress-approval-resolved") {
          setEgressApproval((cur) => (cur && cur.approvalId === payload.approvalId ? null : cur));
        }
        // Sent when a socket attaches and the host holds no request: a card
        // still on screen is for one answered while this client had no socket
        // to hear it. A request still held is re-sent instead, and keeps its
        // card (same key), with the note typed into it.
        if (payload.type === "egress-approval-none") {
          setEgressApproval(null);
        }
      };

      // Wired ONCE and reads the live `ws`, so it survives reconnects.
      const dataDisposable = term.onData(
        (data) =>
          ws?.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: "input", data }))
      );

      // Mirror apps/web/terminal.js:sendResize. Two pieces matter:
      //   1. After fit(), call term.refresh(0, rows-1) so xterm redraws
      //      every cell at the new metrics. Without this, content
      //      previously painted at the old cell positions stays drawn
      //      and bleeds through as artifacts on resize.
      //   2. Debounce the WS resize and dedupe against the last sent
      //      cols/rows so layout transitions don't spam SIGWINCH at
      //      the PTY (which makes the agent re-render its TUI).
      let lastSentCols = -1;
      let lastSentRows = -1;
      let resizeTimer: ReturnType<typeof setTimeout> | null = null;
      const repaintTerminal = () => {
        if (term.rows <= 0) return;
        term.clearTextureAtlas?.();
        term.refresh(0, term.rows - 1);
      };
      const sendResize = () => {
        // VISIBILITY GUARD — never fit/resize a hidden tile. When another tile
        // is enlarged (or this one is a background tab), this tile's wrapper is
        // display:none, so its host measures 0×0. fit() would then compute a
        // ~2-col grid and ship it to the PTY, collapsing the backgrounded agent
        // to ~11 cols; restoring the tile leaves it stuck there with the text
        // wrapped/fragmented at the tiny old width. A 0×0 element also means
        // "layout not settled yet" during mount, so skipping here doubles as a
        // race guard. offsetWidth/Height are 0 for any display:none ancestor.
        const el = hostRef.current;
        if (!el || el.offsetWidth === 0 || el.offsetHeight === 0) return;
        try {
          fit.fit();
          repaintTerminal();
        } catch {
          // fit/refresh failed (renderer mid-teardown or canvas context lost).
          // Don't return — proceed to the WS send so the PTY gets the resize
          // if term.cols/rows are still valid. The dedup check below handles
          // the case where dimensions didn't actually change.
          if (term.rows <= 0) return;
        }
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        if (term.cols === lastSentCols && term.rows === lastSentRows) return;
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          resizeTimer = null;
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          if (term.cols === lastSentCols && term.rows === lastSentRows) return;
          lastSentCols = term.cols;
          lastSentRows = term.rows;
          ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
        }, 40);
      };
      const observer = new ResizeObserver(() => sendResize());
      observer.observe(hostRef.current);
      // Expose the live sendResize so the layout-nudge effect can force a
      // post-layout re-fit that the ResizeObserver may have raced past.
      refitRef.current = sendResize;

      // While detached (see connect()), look every NOT_OWNED_RETRY_MS for the
      // terminal in the caller's own persisted listing, and reattach once it is
      // there. The listing hands the caller only records that name no owner,
      // and this terminal's record names its owner, so looking never takes it
      // over while the record exists; a reattach would, once the record were
      // lost (src/server.js lets whoever reattaches such an id have it). Both
      // continuations below return at once if the pane has been closed.
      function awaitOwnAgain() {
        if (disposed) return;
        reconnectTimer = setTimeout(async () => {
          const own = await api
            .listPersistedTerminals()
            .then((records) => records.some((record) => record.id === terminal.id))
            .catch(() => false);
          if (own) void connect(true);
          else awaitOwnAgain();
        }, NOT_OWNED_RETRY_MS);
      }

      async function connect(reattach: boolean) {
        if (disposed || !hostRef.current) return;
        // A reconnect whose previous socket never opened almost always means
        // the host RESTARTED and lost its in-memory terminal map, so the /io
        // upgrade 404s until the surviving tmux/daemon session is re-adopted.
        // Re-POST the terminal with reattachId first — idempotent if it's still
        // mapped (the host returns the existing terminal), so a transient drop
        // pays only one extra request. If the host is still down the POST
        // throws and the WS open below fails → normal backoff retries.
        if (reattach) {
          try {
            await api.spawnTerminal({ agent: terminal.agent, reattachId: terminal.id });
          } catch (error) {
            // Not this identity's: the host answers so for another user's
            // terminal, or this user's in another org. A pane only holds a
            // terminal its user opened, so the session now names another org
            // (switched here or in another tab) while the agent lives on.
            // Rather than end the pane, wait until it is the caller's again
            // (awaitOwnAgain), then reattach.
            if (isNotOwnedRestoreRefusal(error)) {
              if (!detached) {
                detached = true;
                term.writeln(
                  "\r\n[detached: this terminal belongs to another org or account; it reconnects when you switch back]"
                );
              }
              endEgress();
              awaitOwnAgain();
              return;
            }
            // The host refused to respawn it (session gone, and it cannot
            // respawn where it was). No retry can succeed, so stop here rather
            // than reconnect for the life of the tab.
            if (isStaleRestoreRefusal(error)) {
              hasExited = true;
              term.writeln(
                `\r\n[session ended: ${error instanceof Error ? error.message : "stale"}]`
              );
              endEgress();
              return;
            }
            /* host still unreachable; the WS open below will fail → retry */
          }
          if (disposed || !hostRef.current) return;
        }
        // Re-mint the Clerk "convex"-template JWT on EVERY connect: Convex auth
        // needs that audience and a browser WS can't send it as a header, so it
        // rides as a `ct` query param. Re-minting matters on reconnect — the
        // prior token may have expired. On failure we connect tokenless; the
        // host falls back to the session cookie and fails closed if Convex
        // rejects it.
        let convexToken: string | null = null;
        try {
          convexToken = (await getTokenRef.current?.({ template: "convex" })) ?? null;
        } catch {
          /* connect tokenless; host fails closed if it can't verify */
        }
        if (disposed || !hostRef.current) return;

        const socket = new WebSocket(terminalWebSocketUrl(terminal.id, convexToken));
        ws = socket;
        // Did THIS socket reach 'open'? A close without an open means the
        // upgrade was rejected (404 after a host restart, or refused while the
        // host is down) → the next attempt must reattach before reconnecting.
        let opened = false;
        socket.addEventListener("message", handleMessage);
        socket.addEventListener("open", () => {
          opened = true;
          detached = false;
          if (reconnectAttempts > 0) {
            term.write("\r\n\x1b[2m── reconnected ──\x1b[0m\r\n");
          }
          reconnectAttempts = 0;
          // Force a resize push so the freshly (re)attached PTY is sized to the
          // current tile even if cols/rows didn't change since last time.
          //
          // CRITICAL: an absolutely-positioned tile (tiling/floating/canvas)
          // can finish its layout a frame or two AFTER the socket opens. A
          // single immediate fit then measures a transient, pre-layout box —
          // often only ~10 cols — sends that, and the cols/rows dedup LOCKS the
          // PTY (and the xterm grid) at that wrong narrow size forever, since
          // term.cols never "changes" again. That's the "terminal stuck narrow /
          // text wraps at ~10 cols inside a wide tile after reattach" bug, and
          // it hits some tiles but not others purely by layout-timing luck.
          // Re-fit across several settle points so the final, correct
          // measurement wins and overrides the stale dedup.
          lastSentCols = -1;
          lastSentRows = -1;
          sendResize();
          // Cancel any still-pending batch from a previous open (rapid
          // reconnect) before scheduling a fresh one, and track these so
          // cleanup() can cancel them on unmount.
          if (openRaf != null) cancelAnimationFrame(openRaf);
          openTimers.forEach(clearTimeout);
          openTimers.length = 0;
          openRaf = requestAnimationFrame(() => sendResize());
          openTimers.push(
            setTimeout(() => sendResize(), 120),
            setTimeout(() => sendResize(), 400),
            setTimeout(() => sendResize(), 900)
          );
        });
        socket.addEventListener("close", () => {
          // Ignore the close of a socket we've already replaced, any close after
          // teardown (cleanup nulls `ws` before closing), or a terminal that
          // exited on its own (reconnecting would respawn a fresh shell).
          if (disposed || ws !== socket || hasExited) return;
          reconnectAttempts += 1;
          if (reconnectAttempts === 1) {
            term.write("\r\n\x1b[2m── disconnected, reconnecting… ──\x1b[0m\r\n");
          }
          const delay = Math.min(500 * 2 ** (reconnectAttempts - 1), 8000);
          reconnectTimer = setTimeout(() => void connect(!opened), delay);
        });
      }

      cleanup = () => {
        observer.disconnect();
        refitRef.current = null;
        termRef.current = null;
        unsubscribeTheme();
        unregisterPaste();
        if (resizeTimer) clearTimeout(resizeTimer);
        if (reconnectTimer) clearTimeout(reconnectTimer);
        if (openRaf != null) cancelAnimationFrame(openRaf);
        openTimers.forEach(clearTimeout);
        dataDisposable.dispose();
        fileLinksDispose?.();
        const closing = ws;
        ws = null; // null first so the close handler treats this as a teardown
        closing?.close();
        term.dispose();
      };

      // Initial connect: the terminal was just spawned, so it's already in the
      // host's map — no reattach needed.
      void connect(false);
    }

    void attach();
    return () => {
      disposed = true;
      cleanup();
    };
  }, [terminal.id]);

  // After a layout-driven tile resize (tiling/floating/canvas splitter drag, a
  // mode switch, density change), re-fit once the browser has committed the new
  // box. requestAnimationFrame defers past React's paint so fit() measures the
  // FINAL size — closing the gap where the ResizeObserver's debounced send
  // raced the commit and left the PTY stuck at the old cols/rows. Idempotent
  // with the observer path: both funnel through sendResize, which dedupes
  // against the last cols/rows actually sent, so a redundant nudge is a no-op.
  useEffect(() => {
    if (!nudge) return;
    const raf = requestAnimationFrame(() => refitRef.current?.());
    return () => cancelAnimationFrame(raf);
  }, [nudge]);

  // Enlarge/restore changes the tile size via a CSS override (.is-enlarged),
  // NOT via the position prop, so the nudge effect above doesn't see it. Re-fit
  // on the transition so the PTY tracks the new size, and on ENLARGE restore
  // keyboard focus — clicking the ⤢ button blurs the terminal, which is why the
  // enlarged tile "can't be typed in" until you click back into it.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      refitRef.current?.();
      termRef.current?.clearTextureAtlas?.();
      if (termRef.current && termRef.current.rows > 0) {
        termRef.current.refresh(0, termRef.current.rows - 1);
      }
      if (isEnlarged) termRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isEnlarged]);

  // Drag-of-selection: press inside the current selection and move → drag a
  // ghost chip; drop onto any terminal pastes there (the macOS-native
  // "press-hold the highlight, then drag" gesture). Press OUTSIDE the
  // selection → we don't interfere and xterm makes a new selection. The
  // listener is capture-phase so it can pre-empt xterm clearing the selection
  // on mousedown when we decide this is a drag.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // Gesture states: idle → pressing (a left-press began INSIDE the selection)
    // → dragging (the pointer then moved past DRAG_THRESHOLD). A press that
    // releases WITHOUT crossing the threshold is a plain click, NOT a drag, so
    // it must not drop/paste: otherwise clicking inside a selection re-pastes it
    // into the terminal — and a selection containing a newline would be
    // EXECUTED at the prompt. (Codex P1.)
    const DRAG_THRESHOLD = 4; // px of pointer travel before a press becomes a drag
    let pressing = false;
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let text = "";
    let ghost: HTMLDivElement | null = null;

    const inSelection = (clientX: number, clientY: number): boolean => {
      const term = termRef.current;
      if (!term || !term.hasSelection()) return false;
      const pos = term.getSelectionPosition();
      if (!pos) return false;
      const screen = host.querySelector(".xterm-screen") as HTMLElement | null;
      if (!screen) return false;
      return pointInSelection(
        clientX,
        clientY,
        screen.getBoundingClientRect(),
        term.cols || 80,
        term.rows || 24,
        pos
      );
    };
    const moveGhost = (x: number, y: number) => {
      if (ghost) {
        // offset from the cursor hotspot so the ghost doesn't cover the drop point
        ghost.style.left = `${x + 8}px`;
        ghost.style.top = `${y + 8}px`;
      }
    };
    const teardown = () => {
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("pointercancel", onPointerCancel, true);
      if (ghost) {
        ghost.remove();
        ghost = null;
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!pressing) return;
      if (!dragging) {
        // Promote to a drag only once the pointer has actually moved — a
        // couple of px of jitter on a click stays a click.
        if (
          Math.abs(e.clientX - startX) < DRAG_THRESHOLD &&
          Math.abs(e.clientY - startY) < DRAG_THRESHOLD
        ) {
          return;
        }
        dragging = true;
        ghost = document.createElement("div");
        ghost.className = "terminal-drag-ghost";
        ghost.textContent =
          text.length > GHOST_MAX_CHARS ? text.slice(0, GHOST_MAX_CHARS) + "…" : text;
        document.body.appendChild(ghost);
      }
      moveGhost(e.clientX, e.clientY);
    };
    const onPointerUp = (e: PointerEvent) => {
      const wasDragging = dragging;
      teardown();
      pressing = false;
      dragging = false;
      if (wasDragging) {
        dropDraggedText(text, e.clientX, e.clientY);
      } else {
        // Plain click inside the selection (no movement): mimic the native
        // "click clears the highlight" behaviour rather than pasting.
        termRef.current?.clearSelection();
      }
    };
    const onPointerCancel = () => {
      // A system gesture / scroll / modal interrupted the press: abandon it
      // with no drop and full cleanup (pointerup never fires after cancel, so
      // the ghost + window listeners would otherwise leak). (Gemini.)
      teardown();
      pressing = false;
      dragging = false;
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return; // left button only (right-click = context menu)
      // Option (⌥) is xterm's force-raw-selection modifier; let it always reach
      // xterm to start a NEW selection rather than hijacking it into a drag of
      // the existing one.
      if (e.altKey) return;
      if (!inSelection(e.clientX, e.clientY)) return; // let xterm reselect
      const term = termRef.current;
      if (!term) return;
      text = term.getSelection();
      if (!text) return;
      e.preventDefault();
      e.stopPropagation(); // stop xterm clearing the selection on mousedown
      pressing = true;
      dragging = false;
      startX = e.clientX;
      startY = e.clientY;
      // Ghost is created lazily in onPointerMove once the threshold is crossed.
      window.addEventListener("pointermove", onPointerMove, true);
      window.addEventListener("pointerup", onPointerUp, true);
      window.addEventListener("pointercancel", onPointerCancel, true);
    };

    const onMouseDown = (e: MouseEvent) => {
      // xterm's selection runs off `mousedown`, a SEPARATE event that
      // stopPropagation on pointerdown does NOT suppress. Suppress it for the
      // whole press (pointerdown → release) so the terminal neither clears the
      // highlight nor rubber-bands a new selection under the press. Clicks
      // outside a selection never set `pressing`, so they pass through.
      if (pressing) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    host.addEventListener("pointerdown", onPointerDown, true); // capture: before xterm
    host.addEventListener("mousedown", onMouseDown, true);
    return () => {
      host.removeEventListener("pointerdown", onPointerDown, true);
      host.removeEventListener("mousedown", onMouseDown, true);
      teardown();
    };
  }, [terminal.id]);

  return (
    <div
      ref={hostRef}
      data-terminal-id={terminal.id}
      style={{ width: "100%", height: "100%", position: "relative" }}
      onContextMenu={(e) => {
        e.preventDefault();
        const MENU_W = 160;
        const MENU_H = 80;
        setMenu({
          x: Math.min(e.clientX, window.innerWidth - MENU_W),
          y: Math.min(e.clientY, window.innerHeight - MENU_H),
          // Snapshot selection state at open time; the canvas won't change
          // while the menu is open.
          canCopy: Boolean(termRef.current?.hasSelection()),
        });
      }}
    >
      {menu && (
        <TerminalContextMenu
          x={menu.x}
          y={menu.y}
          canCopy={menu.canCopy}
          onCopy={doCopy}
          onPaste={doPaste}
          onClose={closeMenu}
        />
      )}
      {egressApproval && (
        <div
          style={{ position: "absolute", left: 8, right: 8, bottom: 8, zIndex: 20 }}
          onContextMenu={(e) => e.stopPropagation()}
        >
          {/* Keyed by the request, so a newer ask gets a fresh card even when it
              replaces the old one in a single render: the card keeps its busy
              state after a decision, expecting to unmount. */}
          <EgressApprovalCard
            key={egressApproval.approvalId}
            host={egressApproval.host}
            command={egressApproval.command}
            reason={egressApproval.reason}
            onDecide={decideEgress}
          />
        </div>
      )}
      {egressNotice && !egressApproval && (
        <div
          className="approval-card egress-approval-notice"
          role="status"
          style={{ position: "absolute", left: 8, right: 8, bottom: 8, zIndex: 20 }}
          onContextMenu={(e) => e.stopPropagation()}
        >
          <p className="approval-card__reason">{egressNotice}</p>
          <div className="approval-card__actions">
            <button type="button" onClick={() => setEgressNotice(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function agentOptions(names: string[]) {
  const defaults = ["Regular Terminal", "Claude Code", "Codex", "Gemini CLI", "Cursor", "opencode"];
  return Array.from(new Set([...defaults, ...names]));
}

// Height (px) of the per-tile header (.terminal-pane__tile-header). box-sizing
// is border-box globally, so min-height:26px already includes the 1px
// border-bottom. Every tile in every layout now carries this header, so the
// xterm body is the tile height minus the host's vertical padding AND this
// header. measureInitialDims subtracts both — otherwise the pre-flight measures
// ~1–2 rows too many and the agent's TUI paints its splash for rows that get
// clipped until the first post-mount resize corrects them.
const TILE_HEADER_PX = 26;

// Predict the pixel size of the tile that will host the next-spawned terminal,
// per layout mode. Matches the buildLayoutModel + CSS-grid math (gap=10, pad=10,
// host inner padding ≈ 20×16) closely enough that the temp xterm mounted at
// this size measures the same cols/rows the live tile will end up with.
function estimateTileSize(
  hostsEl: HTMLDivElement,
  layout: string,
  density: number,
  totalTiles: number,
  canvasTileW?: number,
  canvasTileH?: number
): { width: number; height: number } {
  const cw = hostsEl.clientWidth || 800;
  const ch = hostsEl.clientHeight || 480;
  const gap = 10;
  const pad = 10;

  if (layout === "tabs") {
    return { width: Math.max(120, cw - pad * 2), height: Math.max(80, ch - pad * 2) };
  }
  if (layout === "floating") {
    // Mirror buildLayoutModel lines 470-471: responsive default window size.
    const defaultW = Math.min(540, Math.max(360, Math.round(cw * 0.45)));
    const defaultH = Math.min(420, Math.max(280, Math.round(ch * 0.5)));
    return { width: defaultW, height: defaultH };
  }
  if (layout === "canvas") {
    // Use actual canvas tile dimensions from the layout context when provided.
    return { width: canvasTileW || 600, height: canvasTileH || 400 };
  }
  if (layout === "tiling") {
    // Approximate a balanced grid from the tile count — better than always
    // assuming a 2-pane split when 3+ tiles are open in an asymmetric tree.
    const side = Math.max(2, Math.ceil(Math.sqrt(totalTiles)));
    return {
      width: Math.max(200, Math.floor(cw / side) - pad),
      height: Math.max(150, Math.floor(ch / side) - pad),
    };
  }

  let cols = 1;
  if (layout === "grid-auto") {
    cols = Math.max(1, Math.floor(cw / Math.max(80, density)));
    cols = Math.min(cols, totalTiles);
  } else if (layout.startsWith("grid-")) {
    cols = parseInt(layout.replace("grid-", ""), 10) || 1;
  }
  const rows = Math.max(1, Math.ceil(totalTiles / cols));
  return {
    width: Math.max(120, Math.floor((cw - pad * 2 - (cols - 1) * gap) / cols)),
    height: Math.max(80, Math.floor((ch - pad * 2 - (rows - 1) * gap) / rows)),
  };
}

function measureSpawnTileBodySize(
  hostsEl: HTMLDivElement,
  layout: string,
  tile: { width: number; height: number }
): { width: number; height: number } | null {
  const host = document.createElement("div");
  const header = document.createElement("div");
  const body = document.createElement("div");

  host.className = "terminal-pane__host is-active";
  header.className = "terminal-pane__tile-header";
  body.className = "terminal-pane__tile-body";
  header.textContent = "Terminal";
  host.style.visibility = "hidden";
  host.style.pointerEvents = "none";
  host.style.zIndex = "-1";

  if (layout === "tiling" || layout === "floating" || layout === "canvas") {
    host.style.position = "absolute";
    host.style.left = "-99999px";
    host.style.top = "0";
    host.style.width = `${tile.width}px`;
    host.style.height = `${tile.height}px`;
  }

  host.append(header, body);
  hostsEl.append(host);
  try {
    const width = body.clientWidth;
    const height = body.clientHeight;
    if (width >= 40 && height >= 40) return { width, height };
    return null;
  } finally {
    host.remove();
  }
}

// Mount a throwaway xterm into an offscreen div sized like the eventual tile
// and ask the FitAddon for the cols/rows. Used to pre-flight the spawn POST
// with realistic dimensions, so the agent's TUI is born at the width it will
// be viewed at. Falls back to 80×24 if anything goes wrong.
async function measureInitialDims(
  hostsEl: HTMLDivElement | null,
  layoutCtx: {
    layout: string;
    density: number;
    totalTiles: number;
    canvasTileW?: number;
    canvasTileH?: number;
  }
): Promise<{ cols: number; rows: number }> {
  const fallback = { cols: 80, rows: 24 };
  if (!hostsEl) return fallback;
  const tile = estimateTileSize(
    hostsEl,
    layoutCtx.layout,
    layoutCtx.density,
    layoutCtx.totalTiles,
    layoutCtx.canvasTileW,
    layoutCtx.canvasTileH
  );
  const measuredBody = measureSpawnTileBodySize(hostsEl, layoutCtx.layout, tile);
  const innerW = Math.max(40, measuredBody?.width ?? tile.width - 20);
  const innerH = Math.max(40, measuredBody?.height ?? tile.height - 16 - TILE_HEADER_PX);

  type XTerm = import("@xterm/xterm").Terminal;
  let div: HTMLDivElement | null = null;
  let term: XTerm | null = null;
  try {
    const [{ Terminal }, { FitAddon }] = await Promise.all([
      import("@xterm/xterm"),
      import("@xterm/addon-fit"),
    ]);
    await ensureTerminalFont();
    div = document.createElement("div");
    div.style.cssText = `position: absolute; left: -99999px; top: 0; width: ${innerW}px; height: ${innerH}px;`;
    document.body.appendChild(div);

    term = new Terminal({
      ...XTERM_FONT,
      lineHeight: 1.0,
      letterSpacing: 0,
      cursorBlink: false,
      scrollback: 0,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(div);
    fit.fit();
    if (term.cols >= 2 && term.rows >= 2) return { cols: term.cols, rows: term.rows };
    return fallback;
  } catch {
    return fallback;
  } finally {
    if (term) term.dispose();
    if (div && div.parentNode) div.parentNode.removeChild(div);
  }
}
